import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { AuthService } from 'app/core/services/auth.service';
import { NotificationService } from 'app/core/services/prothesis-api/notification.service';
import { PreviewService } from 'app/core/services/prothesis-api/preview.service';
import { WorkingLaboratoryService } from 'app/core/services/prothesis-api/working-laboratory.service';
import { PreviewState } from 'app/pages/laboratory-area/preview/preview-state';
import { AbstractNotificationCardModel } from 'app/shared/components/notification-card/abstract-notification-card/abstract-notification-card.model';
import { AbstractNotificationCardModelHelper } from 'app/shared/components/notification-card/abstract-notification-card/abstract-notification-card.model.helper';
import { DocoonInboundFlowNotificationCardModel } from 'app/shared/components/notification-card/job-notification-card/docoon-inbound-flow-notification-card/docoon-inbound-flow-notification-card.model';
import { PdfViewerComponent } from 'app/shared/components/pdf-viewer/pdf-viewer.component';
import { Area } from 'app/shared/enums/area.enum';
import { SpreadsheetExportFileType } from 'app/shared/enums/spreadsheet-export-file-type.enum';
import { FileHelper } from 'app/shared/helpers/file-helper';
import { CustomDatePipe } from 'app/shared/pipes/custom-date.pipe';
import { ReadNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-notification.view-model';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SidebarComponent } from '../sidebar.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PdfViewerComponent as PdfViewerComponent_1 } from '../../../../shared/components/pdf-viewer/pdf-viewer.component';
import { DocoonInboundFlowNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/docoon-inbound-flow-notification-card/docoon-inbound-flow-notification-card.component';
import { ExportAllNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/export-all-notification-card/export-all-notification-card.component';
import { JournalNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/journal-notification-card/journal-notification-card.component';
import { PdfMergeNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/pdf-merge-notification-card/pdf-merge-notification-card.component';
import { SpreadsheetExportXlsxNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/spreadsheet-export-notification-card/xlsx/spreadsheet-export-xlsx-notification-card.component';
import { SpreadsheetExportPdfNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/spreadsheet-export-notification-card/pdf/spreadsheet-export-pdf-notification-card.component';
import { SpreadsheetDashboardNotificationCardComponent } from '../../../../shared/components/notification-card/job-notification-card/spreadsheet-dashboard-notification-card/spreadsheet-dashboard-notification-card.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-notification-sidebar',
  templateUrl: './notification-sidebar.component.html',
  styleUrls: ['./notification-sidebar.component.scss'],
  imports: [
    MatIconButton,
    MatIcon,

    PdfViewerComponent_1,
    DocoonInboundFlowNotificationCardComponent,
    ExportAllNotificationCardComponent,
    JournalNotificationCardComponent,
    PdfMergeNotificationCardComponent,
    SpreadsheetExportXlsxNotificationCardComponent,
    SpreadsheetExportPdfNotificationCardComponent,
    SpreadsheetDashboardNotificationCardComponent,
    MatButton,
  ],
})
export class NotificationSidebarComponent implements OnDestroy, OnInit {
  @HostBinding('class.bar-closed') public barClosed: boolean;
  @ViewChild('_pdfViewerComponent') private _pdfViewerComponent: PdfViewerComponent;
  public noNotificationsLabel = 'Aucune notification.';
  public notifications: AbstractNotificationCardModel[];
  public spreadsheetExportFileType = SpreadsheetExportFileType;
  public title = this.noNotificationsLabel;

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _customDatePipe = inject(CustomDatePipe);
  private _fuseSidebarService = inject(FuseSidebarService);
  private _notificationService = inject(NotificationService);
  private _previewService = inject(PreviewService);
  private _readNotificationViewModels: ReadNotificationViewModel[];
  private _router = inject(Router);
  private _unsubscribeAll$: Subject<void> = new Subject();
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public constructor() {
    this.barClosed = true;
  }

  public closeClickedHandler(): void {
    this._fuseSidebarService.getSidebar(SidebarComponent.key)?.close();
  }

  public deleteAllNotificationsClickedHandler(): void {
    const uuids = this.notifications.flatMap((x) => (x.uuid ? [x.uuid] : []));
    this._notificationService.deleteCollection(uuids).subscribe(() => {
      this._removeNotifications(uuids);
      this._fuseSidebarService.getSidebar(SidebarComponent.key)?.close();
    });
  }

  public deleteNotificationClickedHandler(notification: AbstractNotificationCardModel): void {
    this._notificationService.delete(notification.uuid).subscribe(() => {
      this._removeNotifications(notification.uuid ? [notification.uuid] : []);
    });
  }

  public downloadAllButtonClickedHandler(spreadsheetExportNotificationUuid: string | null | undefined): void {
    this._notificationService.getExportAll(spreadsheetExportNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadJournalFecButtonClickedHandler(journalNotificationUuid: string | null | undefined): void {
    this._notificationService.getJournalFec(journalNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadJournalPdfButtonClickedHandler(journalNotificationUuid: string | null | undefined): void {
    this._notificationService.getJournalPdf(journalNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadJournalSpreadsheetButtonClickedHandler(journalNotificationUuid: string | null | undefined): void {
    this._notificationService.getJournalSpreadsheet(journalNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadMergedPdfButtonClickedHandler(pdfMergeNotificationUuid: string | null | undefined): void {
    this._notificationService.getMergedPdf(pdfMergeNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadSpreadsheetButtonClickedHandler(spreadsheetExportNotificationUuid: string | null | undefined): void {
    this._notificationService.getSpreadsheetExport(spreadsheetExportNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadSpreadsheetDashboardButtonClickedHandler(spreadsheetDashboardNotificationUuid: string | null | undefined): void {
    this._notificationService.getSpreadsheetDashboard(spreadsheetDashboardNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public downloadZipButtonClickedHandler(pdfMergeNotificationUuid: string | null | undefined): void {
    this._notificationService.getMergedZip(pdfMergeNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.download(blob, filename);
    });
  }

  public markNotificationsAsRead(): void {
    if (this._workingLaboratoryService.tenantUuid && this._readNotificationViewModels?.length > 0) {
      this._notificationService.markAsReadCollection(this._readNotificationViewModels.filter((x) => !x.readDate)).subscribe();
    }
  }

  public ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  public ngOnInit(): void {
    this._notificationService.onNotificationRefreshed$.subscribe((readNotificationViewModels: ReadNotificationViewModel[]) => {
      if (readNotificationViewModels) {
        this.refresh(readNotificationViewModels);
      }
    });

    this._notificationService.onPopUpNotificationReceived$.subscribe((readNotificationViewModels: ReadNotificationViewModel[]) => {
      if (readNotificationViewModels) {
        this._fuseSidebarService.getSidebar(SidebarComponent.key)?.toggleOpenOnly();
      }
    });

    this._authService.isDoneLoading$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((isDoneLoadingFlag) => {
      if (isDoneLoadingFlag && (this._authService.area === Area.Laboratory || this._workingLaboratoryService.exploringFlag)) {
        this._notificationService.startPulling();
      }
    });

    this._workingLaboratoryService.exploredUuid$
      .pipe(takeUntil(this._unsubscribeAll$), distinctUntilChanged())
      .subscribe((laboratoryUuid) => {
        if (laboratoryUuid) {
          this._notificationService.startPulling();
        } else {
          this._notificationService.stopPulling();
          this.notifications = [];
          this._fuseSidebarService.getSidebar(SidebarComponent.key)?.close();
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  public openJournalPdfButtonClickedHandler(journalNotificationUuid: string | null | undefined): void {
    this._notificationService.getJournalPdf(journalNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.open(blob, filename);
    });
  }

  public openMergedPdfButtonClickedHandler(pdfMergeNotificationUuid: string | null | undefined): void {
    this._notificationService.getMergedPdf(pdfMergeNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.open(blob, filename);
    });
  }

  public openSpreadsheetButtonClickHandler(spreadsheetExportNotificationUuid: string | null | undefined): void {
    this._notificationService.getSpreadsheetExport(spreadsheetExportNotificationUuid).subscribe((res) => {
      const { blob, filename }: { blob: Blob; filename: string } = FileHelper.getBlobAndFilename(res);
      this._pdfViewerComponent.open(blob, filename);
    });
  }

  public previewPurchaseCreditsButtonClickedHandler(notification: DocoonInboundFlowNotificationCardModel): void {
    this._previewPurchaseDocuments('/preview/purchase-credit', notification.purchaseCreditUuids);
  }

  public previewPurchaseInvoicesButtonClickedHandler(notification: DocoonInboundFlowNotificationCardModel): void {
    this._previewPurchaseDocuments('/preview/purchase-invoice', notification.purchaseInvoiceUuids);
  }

  public refresh(readNotificationViewModels: ReadNotificationViewModel[]): void {
    if (!this._workingLaboratoryService.tenantUuid) {
      this.title = 'Vous devez choisir un laboratoire à explorer avant de pouvoir accéder à ses données.';
      this._notificationService.stopPulling();
    } else {
      this.title = this.noNotificationsLabel;

      this._readNotificationViewModels = readNotificationViewModels;
      this.notifications = AbstractNotificationCardModelHelper.fromViewModel(
        readNotificationViewModels,
        this._customDatePipe,
        this._workingLaboratoryService.laboratoryLegalIdentifierSchemeCode,
      );

      if (this.notifications.length === 0) {
        this._fuseSidebarService.getSidebar(SidebarComponent.key)?.close();
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  private _previewPurchaseDocuments(command: string, uuids: string[]): void {
    const previewState = new PreviewState();
    previewState.uuids = uuids;
    const routeData = this._previewService.configRouteData(command, previewState);
    this._router.navigate(routeData.commands, routeData.extras);

    // The preview takes the whole page: the sidebar would only hide it.
    this._fuseSidebarService.getSidebar(SidebarComponent.key)?.close();
  }

  private _removeNotifications(uuids: string[]): void {
    // Filtered rather than spliced by index: an uuid already gone (a double click, a poll in between) must not
    // remove an unrelated card.
    this.notifications = this.notifications.filter((n) => !uuids.some((uuid) => uuid === n.uuid));
    this._readNotificationViewModels = this._readNotificationViewModels.filter((n) => !uuids.some((uuid) => uuid === n.uuid));

    if (this.notifications.length === 0) {
      this._fuseSidebarService.getSidebar(SidebarComponent.key)?.close();
    }

    this._changeDetectorRef.markForCheck();
  }
}
